import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Trash2, Tag, Wand2, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import EmailBuilder from "./EmailBuilder";
import AudienceFilterPicker from "./AudienceFilterPicker";

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);

export default function PromotionsCard({ marketingActive }) {
  const [items, setItems] = useState([]);
  const [nextAllowedAt, setNextAllowedAt] = useState(null);
  const [cooldownDays, setCooldownDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", description: "", discount_code: "", expires_at: "" });
  const [sending, setSending] = useState(false);
  const [design, setDesign] = useState(null);
  const [html, setHtml] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [audience, setAudience] = useState({ zips: [], cities: [], states: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/promotions");
      setItems(data.items || []);
      setNextAllowedAt(data.next_allowed_at || null);
      if (data.cooldown_days) setCooldownDays(data.cooldown_days);
    } catch (e) {
      console.warn("PromotionsCard load failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.expires_at) {
      toast.error("Pick an expiration date");
      return;
    }
    if (!html) {
      toast.error("Open the message builder and save a design first.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...form, design_json: design, custom_html: html };
      const hasAudience = (audience.zips?.length || 0) + (audience.cities?.length || 0) + (audience.states?.length || 0) > 0;
      if (hasAudience) payload.audience = audience;
      const { data } = await api.post("/promotions", payload);
      const msg = data.recipients
        ? `Promotion live — messaged ${data.recipients} opted-in customers`
        : "Promotion live (no messages sent: no opted-in customers yet)";
      toast.success(msg);
      setForm({ title: "", description: "", discount_code: "", expires_at: "" });
      setDesign(null);
      setHtml(null);
      setBuilderOpen(false);
      setAudience({ zips: [], cities: [], states: [] });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/promotions/${id}`);
      toast.success("Promotion deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Card className="rounded-md border border-border p-6" data-testid="promotions-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone size={18} className="text-[#B31E1E]" />
            <h3 className="font-display text-2xl">Special offers</h3>
            {!marketingActive && (
              <span className="px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                Marketing add-on
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Send promotions and discounts to every opted-in customer on Servrevu. We deliver on your behalf — you never see their contact addresses. <span className="text-gray-700 font-medium">Limit: 1 promotional message per business per {cooldownDays} days.</span>
          </p>
        </div>
        {marketingActive && nextAllowedAt && (
          <div
            className="text-[11px] font-mono uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-md"
            data-testid="promotion-cooldown-badge"
            title={`Next send available ${new Date(nextAllowedAt).toLocaleString()}`}
          >
            Next send {new Date(nextAllowedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </div>
        )}
      </div>

      {!marketingActive && (
        <div className="mt-5 border border-dashed border-amber-300 bg-amber-50/50 rounded-md p-4 text-sm">
          <p>Messaging customers is part of the Marketing add-on ($35/mo, on top of servrevu Pro).</p>
          <Link to="/subscription">
            <Button className="mt-3 rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="promotion-upgrade-cta">
              Add Marketing — $35/mo →
            </Button>
          </Link>
        </div>
      )}

      {marketingActive && nextAllowedAt && (
        <div className="mt-5 border border-amber-200 bg-amber-50 rounded-md p-4 text-sm" data-testid="promotion-cooldown-banner">
          <p className="text-amber-900">
            <strong>Weekly send limit reached.</strong> To protect inboxes, each business can send 1 promotional message every {cooldownDays} days. Your next message can go out on{" "}
            <strong>{new Date(nextAllowedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</strong>.
          </p>
        </div>
      )}

      {marketingActive && !nextAllowedAt && (
        <div className="mt-5" data-testid="promotion-compose">
          <form onSubmit={submit} className="border border-border rounded-md p-4 bg-gray-50/50 space-y-4" data-testid="promotion-builder-form">
            <p className="text-xs text-gray-500">
              Fill in the headline, description, optional discount code and expiration. The metadata is used for the email subject line, public offer card and Servrevu offer page. Design the body below with the drag-and-drop builder.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="pf-title-b">Headline (used in subject line)</Label>
                <Input id="pf-title-b" required maxLength={120} placeholder="20% off any pool cleaning this month" value={form.title} onChange={set("title")} className="rounded-md mt-1" data-testid="promo-title-builder" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="pf-desc-b">Short description (shown on public offer card)</Label>
                <Textarea id="pf-desc-b" required rows={2} maxLength={1500} placeholder="Limit one per household." value={form.description} onChange={set("description")} className="rounded-md mt-1" data-testid="promo-description-builder" />
              </div>
              <div>
                <Label htmlFor="pf-code-b">Discount code (optional)</Label>
                <Input id="pf-code-b" maxLength={40} placeholder="POOL20" value={form.discount_code} onChange={set("discount_code")} className="rounded-md mt-1" data-testid="promo-code-builder" />
              </div>
              <div>
                <Label htmlFor="pf-exp-b">Expires on</Label>
                <Input id="pf-exp-b" type="date" required min={TODAY_ISO()} value={form.expires_at} onChange={set("expires_at")} className="rounded-md mt-1" data-testid="promo-expires-builder" />
              </div>
            </div>

            {/* Builder state */}
            {!builderOpen && !html && (
              <div className="border border-dashed border-border rounded-md p-6 text-center bg-white">
                <Wand2 size={22} className="mx-auto text-[#B31E1E]" />
                <p className="mt-2 text-sm text-gray-700">Drag-and-drop blocks (images, headers, buttons, CTAs) to design your email body.</p>
                <Button type="button" onClick={() => setBuilderOpen(true)} className="mt-3 rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] shadow-sm hover:shadow-md" data-testid="email-builder-open">
                  <Wand2 size={13} className="mr-1.5" /> Open email builder
                </Button>
              </div>
            )}

            {!builderOpen && html && (
              <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="email-design-saved">
                <div className="flex items-start gap-2">
                  <Check size={16} className="text-emerald-700 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-emerald-900">Email design saved</p>
                    <p className="text-xs text-emerald-800 mt-0.5">{(html.length / 1024).toFixed(1)} KB — ready to send. The Servrevu unsubscribe footer is appended automatically.</p>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setBuilderOpen(true)} className="rounded-[35px]" data-testid="email-builder-edit">
                  <Pencil size={12} className="mr-1.5" /> Edit
                </Button>
              </div>
            )}

            {builderOpen && (
              <EmailBuilder
                businessName={form.title || ""}
                initialDesign={design}
                onSave={(d, h) => { setDesign(d); setHtml(h); setBuilderOpen(false); toast.success("Design saved"); }}
                onCancel={() => setBuilderOpen(false)}
              />
            )}

            <AudienceFilterPicker value={audience} onChange={setAudience} />

            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={sending || !html}
                className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
                data-testid="promo-send-builder"
              >
                {sending ? "Sending…" : "Publish & email"}
              </Button>
              {html && (
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-[35px]"
                  onClick={() => { setForm({ title: "", description: "", discount_code: "", expires_at: "" }); setDesign(null); setHtml(null); setBuilderOpen(false); }}
                  data-testid="promo-reset-builder"
                >
                  Reset
                </Button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="mt-6">
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500">No offers yet.</p>
        ) : (
          <ul className="space-y-3" data-testid="promotion-list">
            {items.map((p) => (
              <li
                key={p.id}
                className={`border border-border rounded-md p-4 ${p.is_active ? "bg-white" : "bg-gray-50 opacity-75"}`}
                data-testid={`promotion-item-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium">{p.title}</h4>
                      {!p.is_active && <Badge variant="outline" className="rounded-md text-[10px] font-mono">Expired</Badge>}
                      {p.discount_code && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-[#B31E1E]/8 text-[#B31E1E] px-2 py-0.5 rounded-md">
                          <Tag size={10} /> {p.discount_code}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600 line-clamp-2">{p.description}</p>
                    <p className="mt-2 text-[11px] font-mono text-gray-400">
                      Expires {p.expires_at} · Emailed {p.recipients_count} customer{p.recipients_count === 1 ? "" : "s"}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-[35px] text-red-600 hover:bg-red-50"
                    onClick={() => remove(p.id)}
                    data-testid={`promo-delete-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
